/**
 * Graceful Creations — quantity price breaks
 *
 * Drives Tinker's own quantity selector from a ladder of tier tiles, and
 * keeps the ladder's selected state and next-tier nudge in step with
 * whatever the quantity currently is — whether that came from a tile, the
 * stepper buttons or someone typing in the field.
 *
 * This block submits nothing. The saving is applied by a native Shopify
 * automatic discount at cart and checkout; the tiles only state what those
 * discounts are and set the quantity.
 *
 * Implemented as a custom element so each block instance wires itself up on
 * connect. That matters in the theme editor, which tears down and re-renders
 * blocks on every settings change — a one-shot DOMContentLoaded listener
 * would go dead after the first edit.
 */

/**
 * Fills {placeholders} in a merchant-entered string, returning DOM nodes
 * rather than markup: the replaced values come back wrapped in <b>, and
 * the surrounding copy stays plain text. Building it out of text nodes
 * means a settings string can never inject markup, which innerHTML on the
 * same value would allow.
 *
 * @param {string} template
 * @param {Record<string, string | number>} values
 * @returns {DocumentFragment}
 */
function fill(template, values) {
  const frag = document.createDocumentFragment();
  const parts = String(template || '').split(/(\{[a-z]+\})/g);

  for (const part of parts) {
    if (!part) continue;
    const token = part.match(/^\{([a-z]+)\}$/);

    if (token && Object.prototype.hasOwnProperty.call(values, token[1])) {
      const strong = document.createElement('b');
      strong.textContent = String(values[token[1]]);
      frag.appendChild(strong);
    } else {
      frag.appendChild(document.createTextNode(part));
    }
  }

  return frag;
}

class GcQuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-gc-qb-root]');
    if (!this.root) return;

    const tiles = Array.from(this.querySelectorAll('[data-gc-qb-tier]'));

    /** Sorted by quantity, so the base tile is always first. */
    this.tiers = tiles
      .map((el) => ({
        el,
        min: parseInt(el.dataset.min, 10) || 1,
        pct: parseFloat(el.dataset.pct) || 0,
        saving: el.dataset.saving || '',
      }))
      .sort((a, b) => a.min - b.min);

    // A ladder with nothing but the full-price tile has nothing to say.
    // Kept visible in the editor so tiers can be added to it.
    if (this.tiers.length < 2 && !this.root.dataset.designMode) {
      this.root.hidden = true;
      return;
    }

    this.nudge = this.querySelector('[data-gc-qb-nudge]');
    this.nudgeText = this.querySelector('[data-gc-qb-nudge-text]');

    this.#findQuantity();
    this.#labelBaseTier();

    for (const tier of this.tiers) {
      tier.el.addEventListener('click', () => this.#setQuantity(tier.min));
    }

    if (this.input) {
      // The stepper writes straight to the input, so watch the input
      // rather than the buttons.
      this.input.addEventListener('change', () => this.render());
      this.input.addEventListener('input', () => this.render());
    }

    this.render();
  }

  /**
   * Tinker's quantity block is a sibling, not a descendant, so look within
   * the surrounding section rather than inside this element.
   */
  #findQuantity() {
    const scope = this.closest('.shopify-section') || document;

    this.selector = scope.querySelector('quantity-selector-component');
    this.input = this.selector
      ? this.selector.querySelector('input[name="quantity"]')
      : null;
  }

  /**
   * The base tile ships labelled "1" because Liquid can't see the child
   * tiers. Now that we can, give it its real upper bound.
   */
  #labelBaseTier() {
    const label = this.querySelector('[data-gc-qb-base-label]');
    if (!label) return;

    const first = this.tiers.find((tier) => tier.min > 1);
    if (!first) return;

    // en dash, matching the lead-time copy
    label.textContent = first.min > 2 ? `1–${first.min - 1}` : '1';
  }

  /** @returns {number} */
  #quantity() {
    if (this.selector && typeof this.selector.getValue === 'function') {
      return parseInt(this.selector.getValue(), 10) || 1;
    }
    return this.input ? parseInt(this.input.value, 10) || 1 : 1;
  }

  /**
   * Goes through the component's public API where it exists, so quantity
   * rules and the stepper's own button states stay Tinker's business.
   *
   * @param {number} quantity
   */
  #setQuantity(quantity) {
    if (this.selector && typeof this.selector.setValue === 'function') {
      this.selector.setValue(String(quantity));
    } else if (this.input) {
      this.input.value = String(quantity);
    } else {
      // No quantity block on this template. The tiles are inert, but the
      // ladder still reads as a price list, so leave it be.
      return;
    }

    if (this.selector && typeof this.selector.updateButtonStates === 'function') {
      this.selector.updateButtonStates();
    }

    if (this.input) {
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.render();
  }

  /**
   * The tier that wins is the biggest discount the order qualifies for,
   * not the last one in the list — which is how Shopify resolves competing
   * automatic discounts of the same class, and means a ladder entered out
   * of order still shows the customer the price they'll actually pay.
   *
   * @param {number} quantity
   */
  #winner(quantity) {
    let best = this.tiers[0];

    for (const tier of this.tiers) {
      if (quantity >= tier.min && tier.pct >= best.pct) best = tier;
    }

    return best;
  }

  render() {
    const quantity = this.#quantity();
    const winner = this.#winner(quantity);
    const next = this.tiers.find(
      (tier) => tier.min > quantity && tier.pct > winner.pct
    );

    for (const tier of this.tiers) {
      tier.el.setAttribute('aria-pressed', tier === winner ? 'true' : 'false');
    }

    if (!this.nudge || !this.nudgeText) return;

    if (next) {
      this.nudge.hidden = false;
      this.nudge.classList.remove('is-max');
      this.nudgeText.replaceChildren(
        fill(this.root.dataset.nudge, {
          n: next.min - quantity,
          min: next.min,
          pct: next.pct,
          save: next.saving,
        })
      );
    } else if (winner.pct > 0) {
      this.nudge.hidden = false;
      this.nudge.classList.add('is-max');
      this.nudgeText.replaceChildren(
        fill(this.root.dataset.nudgeMax, {
          pct: winner.pct,
          save: winner.saving,
        })
      );
    } else {
      // Below the first tier and nothing to reach for: say nothing.
      this.nudge.hidden = true;
    }
  }
}

if (!customElements.get('gc-quantity-breaks-component')) {
  customElements.define('gc-quantity-breaks-component', GcQuantityBreaks);
}
